import { edgeId, faceNormal, propagateExplicitEdgeIds } from '../edges.js';
import { assertSafeIdCapacity } from '../ids.js';
import { coplanarRegion } from '../regions.js';
import { assertRenderableTriangle } from '../validation.js';

/**
 * ---------------------------------------------------------------------------
 * Conforming subdivision
 * ---------------------------------------------------------------------------
 *
 * One primitive, `computeConformingSubdivision`, backs both public entry
 * points. It replaces an earlier two-phase shape — "split the region, then
 * patch up single-edge external neighbours" — which could not express the
 * invariants this operation actually has to hold:
 *
 * - Conforming lived in `subdivideRegion` only, so `subdivideFace` tore a
 *   welded mesh open along all three of its edges.
 * - The patch-up pass assumed a neighbour could only ever be affected on one
 *   edge. A coplanar face excluded from the region by *winding* rather than by
 *   plane (a reversed twin / two-sided fin) borders it on two or three, and was
 *   split once per shared edge — independently, each split assuming the others
 *   had not happened — producing overlapping, multiply-covered geometry.
 *
 * The rule here is instead uniform and stated once: **choosing to split a set
 * of faces chooses a set of edges, and every face in the mesh carrying any of
 * those edges is re-triangulated to include their midpoints.** A face carrying
 * all three (every source face, and any reversed twin of one) becomes 4; two,
 * 3; one, 2. Nothing else in the mesh moves.
 *
 * That closure is exactly one level deep, which is what keeps this a
 * subdivision primitive rather than the start of a general remesher: the
 * children of a 1- or 2-edge split introduce new edges only in their own
 * interior, never on the parent's untouched edges, so no face beyond the
 * immediate neighbours can acquire a hanging midpoint and there is nothing to
 * propagate to. See `splitTriangle` for the per-face construction and its
 * winding argument.
 */

/**
 * The children a triangle is replaced by, given which of its own three edges
 * carry a new midpoint: `midpoints[i]` is the midpoint vertex id on the edge
 * from `vertexIds[i]` to `vertexIds[(i + 1) % 3]`, or `null` if that edge is
 * not split. Returns the children plus the index of the "center" child (the
 * one bounded by three midpoints), or `-1` when there isn't one.
 *
 * All three cases are the same construction — walk the source triangle's own
 * cyclic order, substituting midpoints where they exist — so every child
 * inherits the source's exact winding and normal direction, for any
 * non-degenerate source and regardless of the source's own orientation. The
 * three cases also tile the source exactly: no gaps, no overlap, total area
 * preserved.
 *
 *   3 split — the standard 1-to-4 subdivision-connectivity split (the same
 *   construction used for Loop-subdivision steps and icosphere generation):
 *
 *     [x0, m0, m2]  [m0, x1, m1]  [m2, m1, x2]  [m0, m1, m2] <- center
 *
 *   1 split (edge s) — "fan from the vertex opposite the split edge", after
 *   rotating so the split edge is x0 -> x1:
 *
 *     [x0, m0, x2]  [m0, x1, x2]
 *
 *   2 split — after rotating so the *unsplit* edge is x2 -> x0, the corner at
 *   x1 is cut off and the remaining quad x0 -> m0 -> m1 -> x2 is triangulated
 *   from x0. Fanning from x0 specifically (rather than from x2) is an
 *   arbitrary but *fixed* choice, so the output is deterministic — which
 *   replay and multi-viewer convergence both depend on:
 *
 *     [m0, x1, m1]  [x0, m0, m1]  [x0, m1, x2]
 */
function splitTriangle(vertexIds, midpoints) {
  const splitIndexes = [0, 1, 2].filter((i) => midpoints[i] !== null);
  const rotated = (r) => [vertexIds[r], vertexIds[(r + 1) % 3], vertexIds[(r + 2) % 3]];

  if (splitIndexes.length === 3) {
    const [x0, x1, x2] = vertexIds;
    const [m0, m1, m2] = midpoints;
    return { children: [[x0, m0, m2], [m0, x1, m1], [m2, m1, x2], [m0, m1, m2]], centerIndex: 3 };
  }

  if (splitIndexes.length === 2) {
    // Rotate so the one unsplit edge becomes x2 -> x0.
    const unsplit = [0, 1, 2].find((i) => midpoints[i] === null);
    const r = (unsplit + 1) % 3;
    const [x0, x1, x2] = rotated(r);
    const m0 = midpoints[r];
    const m1 = midpoints[(r + 1) % 3];
    return { children: [[m0, x1, m1], [x0, m0, m1], [x0, m1, x2]], centerIndex: -1 };
  }

  // Rotate so the one split edge becomes x0 -> x1.
  const r = splitIndexes[0];
  const [x0, x1, x2] = rotated(r);
  const m0 = midpoints[r];
  return { children: [[x0, m0, x2], [m0, x1, x2]], centerIndex: -1 };
}

/** Write precomputed new vertices/faces into `topology` and remove the superseded faces — the single mutation point every subdivision path funnels through, so "all lookups and validation before any write" only has one place it can be gotten wrong. */
function commitSubdivision(topology, removedFaceIds, newVertices, newFaces, nextVertexId, nextFaceId, explicitEdgeIds) {
  const removedSet = new Set(removedFaceIds);
  topology.vertices.push(...newVertices);
  topology.faces = topology.faces.filter((f) => !removedSet.has(f.id));
  topology.faces.push(...newFaces);
  topology.nextVertexId = nextVertexId;
  topology.nextFaceId = nextFaceId;
  if (explicitEdgeIds !== undefined) topology.explicitEdgeIds = explicitEdgeIds;
}

/**
 * The pure computation behind both public entry points: fully subdivide every
 * face in `sourceFaceIds`, conform every other face that shares any of their
 * edges, and validate the *whole* combined result — including the shared id
 * counters — returning it without writing anything to `topology`. Keeping
 * every lookup, position computation, and validation ahead of the single
 * `commitSubdivision` call is what makes failure atomic.
 *
 * Midpoints are minted once per unique edge across the whole operation, keyed
 * by `edgeId` (direction-independent and injective for any accepted vertex id
 * — see its own comment), and reused by every face that carries that edge.
 * That single dedup is what keeps the result watertight in all three places it
 * matters: between two source faces sharing an interior edge (a box quad's
 * diagonal), between a source face and a conformed neighbour across the
 * region boundary, and between two conformed neighbours meeting at a corner.
 *
 * Original vertices are never moved, duplicated, or removed — only referenced
 * by different (new) faces once their old face is retired. This is stricter
 * than every sibling here (extrude/inset duplicate boundary vertices; scale
 * moves them).
 *
 * Rejected atomically, before anything is written: a degenerate source face
 * (via `faceNormal`, matching `extrudeFaceSet`/`insetFaceSet`'s up-front
 * check rather than `scaleFaceSet`'s more permissive "don't reject what we
 * didn't break" stance — subdividing nothing into more nothing has no useful
 * result); any child face, source or conformed, that would not be renderable;
 * and any mint that would run the id counters past `Number.MAX_SAFE_INTEGER`.
 *
 * Note what is deliberately *not* attempted: reconciling with an edge that is
 * already split on one side only. Subdivision never leaves the mesh in that
 * state — that is precisely the invariant this function exists to hold — so
 * the case is unreachable through supported operations, and healing a
 * T-junction that arrived in hand-authored `addMesh` topology is general
 * non-conforming remeshing, a materially larger problem than this primitive's.
 * The guarantee is that subdivision does not *introduce* one.
 */
function computeConformingSubdivision(topology, sourceFaceIds, commandName) {
  if (!Array.isArray(sourceFaceIds) || sourceFaceIds.length === 0) {
    throw new Error(`${commandName} requires a non-empty array of face ids`);
  }
  const sourceIdSet = new Set(sourceFaceIds);
  if (sourceIdSet.size !== sourceFaceIds.length) {
    throw new Error(`${commandName} received duplicate face ids`);
  }
  // Existence checked first, in call order, so the *first* problem reported
  // is always "unknown face id" — same convention every other multi-face
  // operation here follows. Via one index rather than a scan per id: a region
  // on a dense mesh can hold thousands of faces, and `faces.some()` per id
  // makes this quadratic in mesh size before any real work starts.
  const faceIdIndex = new Set(topology.faces.map((f) => f.id));
  for (const id of sourceFaceIds) {
    if (!faceIdIndex.has(id)) throw new Error(`unknown face id '${id}'`);
  }

  // Deterministic order: the topology's own existing face array order,
  // filtered to the set — for a single face this is trivially just that face.
  const sourceFaces = topology.faces.filter((f) => sourceIdSet.has(f.id));

  // Reject a degenerate source before computing anything from it.
  for (const face of sourceFaces) faceNormal(topology, face.id);

  const positionById = new Map(topology.vertices.map((v) => [v.id, v.position]));

  // 1. The edges this operation splits: every edge of every source face.
  const splitEdges = new Map(); // edgeId -> { a, b }
  for (const face of sourceFaces) {
    const ids = face.vertexIds;
    for (let i = 0; i < 3; i++) {
      const a = ids[i];
      const b = ids[(i + 1) % 3];
      const key = edgeId(a, b);
      if (!splitEdges.has(key)) splitEdges.set(key, { a, b });
    }
  }

  // 2. Every face that must be re-triangulated, and which of its own edges
  //    carry a midpoint. A source face necessarily carries three; anything
  //    else here is a neighbour being conformed.
  const affected = [];
  for (const face of topology.faces) {
    const ids = face.vertexIds;
    const keys = [0, 1, 2].map((i) => {
      const key = edgeId(ids[i], ids[(i + 1) % 3]);
      return splitEdges.has(key) ? key : null;
    });
    const splitCount = keys.filter((key) => key !== null).length;
    if (splitCount === 0) continue;
    // Source faces were validated by `faceNormal` above; a conformed
    // neighbour is reached only through this index, so this is the one place
    // its vertex references get checked before being read.
    for (const vertexId of ids) {
      if (!positionById.has(vertexId)) {
        throw new Error(`face '${face.id}' references unknown vertex '${vertexId}'`);
      }
    }
    affected.push({ face, keys, splitCount, isSource: sourceIdSet.has(face.id) });
  }

  // 3. Exact capacity, before a single id is assigned: one vertex per split
  //    edge, and a known child count per affected face.
  const CHILD_COUNT_BY_SPLIT_COUNT = { 1: 2, 2: 3, 3: 4 };
  const faceMintCount = affected.reduce((total, entry) => total + CHILD_COUNT_BY_SPLIT_COUNT[entry.splitCount], 0);
  assertSafeIdCapacity(topology.nextVertexId, splitEdges.size, topology.nextFaceId, faceMintCount, commandName);

  // 4. Mint the midpoints.
  let nextVertexId = topology.nextVertexId;
  const newVertices = [];
  const midpointByEdge = new Map();
  for (const [key, { a, b }] of splitEdges) {
    const aPos = positionById.get(a);
    const bPos = positionById.get(b);
    const position = [(aPos[0] + bPos[0]) / 2, (aPos[1] + bPos[1]) / 2, (aPos[2] + bPos[2]) / 2];
    const id = `v${nextVertexId++}`;
    midpointByEdge.set(key, id);
    newVertices.push({ id, position });
    positionById.set(id, position);
  }

  const explicitEdgeReplacements = new Map();
  for (const [key, { a, b }] of splitEdges) {
    const midpointId = midpointByEdge.get(key);
    explicitEdgeReplacements.set(key, [edgeId(a, midpointId), edgeId(midpointId, b)]);
  }
  const explicitEdgeIds = propagateExplicitEdgeIds(topology.explicitEdgeIds, explicitEdgeReplacements);

  // 5. Re-triangulate. Source faces first, then conformed neighbours, each in
  //    the topology's own face order — so the ids a pure-region subdivision
  //    hands out do not depend on how many neighbours happened to need
  //    conforming.
  let nextFaceId = topology.nextFaceId;
  const sourceRemovedFaceIds = [];
  const conformedFaceIds = [];
  const sourceChildren = [];
  const conformedChildren = [];
  const centerFaceIds = [];

  for (const sourcePass of [true, false]) {
    for (const entry of affected) {
      if (entry.isSource !== sourcePass) continue;
      const midpoints = entry.keys.map((key) => (key === null ? null : midpointByEdge.get(key)));
      const { children, centerIndex } = splitTriangle(entry.face.vertexIds, midpoints);
      const created = children.map((vertexIds) => ({ id: `f${nextFaceId++}`, vertexIds }));
      for (const child of created) {
        assertRenderableTriangle(
          commandName,
          entry.isSource
            ? `face (from source face '${entry.face.id}')`
            : `conforming split of neighbouring face '${entry.face.id}'`,
          child.vertexIds.map((id) => positionById.get(id)),
        );
      }
      if (entry.isSource) {
        sourceRemovedFaceIds.push(entry.face.id);
        sourceChildren.push(...created);
        centerFaceIds.push(created[centerIndex].id);
      } else {
        conformedFaceIds.push(entry.face.id);
        conformedChildren.push(...created);
      }
    }
  }

  return {
    removedFaceIds: [...sourceRemovedFaceIds, ...conformedFaceIds],
    sourceRemovedFaceIds,
    conformedFaceIds,
    newVertices,
    newFaces: [...sourceChildren, ...conformedChildren],
    centerFaceIds,
    nextVertexId,
    nextFaceId,
    explicitEdgeIds,
  };
}

/**
 * Subdivide one triangular face into 4 via the standard edge-midpoint split,
 * in place — kept as its own export for symmetry with `extrudeFace`/
 * `insetFace`/`scaleFace` and for callers (agent or UI) addressing one
 * specific triangle. For "subdivide the visible face this triangle is part
 * of," see `subdivideRegion`.
 *
 * Only the named face is *subdivided*; but on welded topology its three edges
 * are shared, so every face across one of them is **conformed** — minimally
 * re-triangulated through the same new midpoint (see
 * `computeConformingSubdivision`). Without that, this operation left a
 * T-junction on each neighbour: a crack that renders as a visible seam under
 * any subsequent deformation and that no later operation could reconcile,
 * since the two sides no longer agree on where the edge's vertices are.
 * Conforming is therefore not an extra feature of the region variant, it is
 * part of what subdividing a face on a shared-vertex mesh *means*, and it
 * lives in the shared primitive rather than in either entry point.
 *
 * `removedFaceId` remains the single named source face. `removedFaceIds`/
 * `newFaceIds` cover everything this call retired and created, conformed
 * neighbours included — a caller refreshing a cache needs all of them.
 */
export function subdivideFace(topology, faceId) {
  const computed = computeConformingSubdivision(topology, [faceId], 'subdivideFace');
  commitSubdivision(
    topology,
    computed.removedFaceIds,
    computed.newVertices,
    computed.newFaces,
    computed.nextVertexId,
    computed.nextFaceId,
    computed.explicitEdgeIds,
  );
  return {
    removedFaceId: computed.sourceRemovedFaceIds[0],
    removedFaceIds: computed.removedFaceIds,
    newFaceIds: computed.newFaces.map((f) => f.id),
    centerFaceId: computed.centerFaceIds[0],
    conformedFaceIds: computed.conformedFaceIds,
  };
}

/**
 * Subdivide the connected coplanar region containing `faceId` — the visible
 * face a human clicked, not just the one triangle the click happened to hit.
 * See `coplanarRegion` for discovery and `computeConformingSubdivision` for
 * the split; mirrors `extrudeRegion`/`insetRegion`/`scaleRegion`'s composition
 * of the two. Every edge shared between two member faces (e.g. a box quad's
 * diagonal) mints exactly one midpoint, shared by both, so the region
 * subdivides as one watertight patch; every face across the region's boundary
 * is conformed through those same midpoints, so the rest of the mesh stays
 * watertight too. Reduces to exactly `subdivideFace`'s output when the seed
 * has no coplanar, connected neighbour.
 *
 * `centerFaceId` is a deterministic representative (the first source face's
 * own center child, in the region's discovery order) for selection/chaining —
 * the natural "select this afterward" analog to extrude/inset's `capFaceId`.
 * Unlike extrude, chaining another subdivide from it does *not* subdivide just
 * that one center triangle again: subdivision introduces no new hard edge
 * (every child stays exactly coplanar with its source and siblings), so
 * `coplanarRegion` from any resulting face rediscovers *every* new face from
 * this whole call, the same way a second `insetRegion` rediscovers inset's
 * whole flattened area. Unlike `insetRegion`, that second call does not fail —
 * subdivision has no boundary-only restriction — it simply re-subdivides the
 * entire just-created patch (4x its face count), which may be more than a
 * caller wants. To refine one specific child, address it with `subdivideFace`.
 *
 * `centerFaceIds` stays scoped to the region's own source faces; a conformed
 * neighbour has no "center" in the same sense, and is reported separately in
 * `conformedFaceIds`.
 */
export function subdivideRegion(topology, faceId, options) {
  const regionFaceIds = coplanarRegion(topology, faceId, options);
  const computed = computeConformingSubdivision(topology, regionFaceIds, 'subdivideRegion');
  commitSubdivision(
    topology,
    computed.removedFaceIds,
    computed.newVertices,
    computed.newFaces,
    computed.nextVertexId,
    computed.nextFaceId,
    computed.explicitEdgeIds,
  );
  return {
    removedFaceIds: computed.removedFaceIds,
    newFaceIds: computed.newFaces.map((f) => f.id),
    centerFaceId: computed.centerFaceIds[0],
    centerFaceIds: computed.centerFaceIds,
    conformedFaceIds: computed.conformedFaceIds,
  };
}
